import { useState } from 'react'
import { useManagedGuilds, useMyPlatforms, useObjectives } from '@/hooks/queries'
import { connectPlatform, discordAddBotUrl, startOAuth } from '@/lib/oauth'
import { markWizardResume } from '@/lib/draft-storage'
import { platformName } from '@/lib/text'
import { SkeletonLine } from '../ui/Skeleton'
import { cn } from '@/lib/cn'
import { GiveawayObjective } from '@/models/giveaway'
import type { DiscordGuildRegister } from '@/models/discord-guild'
import { Dropdown } from '../ui/Dropdown'
import { ReferralIcon } from '../giveaway/ReferralIcon'

interface NewObjectiveProps {
  onCancel: () => void
  onCreate: (objective: GiveawayObjective) => void
  /** Persists the in-progress giveaway before the Discord bot round trip. */
  onBeforeLeave: () => void
  /** The giveaway already has a referral option; only one is allowed. */
  hasReferral: boolean
  /** First-come giveaway: options hand out keys, and entry counts don't matter. */
  firstEntries: boolean
}

/** Discord is the only external platform the API supports today. */
const PLATFORM = 'discord'

type EntryKind = 'discord' | 'referral'

/** Port of components/new_giveaway/pages/new_objective.vue */
export function NewObjective({
  onCancel,
  onCreate,
  onBeforeLeave,
  hasReferral,
  firstEntries,
}: NewObjectiveProps) {
  const { data: platforms, isLoading: platformsLoading } = useMyPlatforms()
  const platform = platforms?.find((row) => row.name?.toLowerCase() === PLATFORM)
  const connected = platform?.connected === true
  // The server list is read through the linked account, so asking without one
  // only produces an error toast.
  const { data: guilds = [], isLoading: guildsLoading } = useManagedGuilds({ enabled: connected })
  const isLoading = platformsLoading || (connected && guildsLoading)
  const [leaving, setLeaving] = useState(false)
  const [entriesWorth, setEntriesWorth] = useState(1)
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null)
  const [kind, setKind] = useState<EntryKind>('discord')
  const { data: objectiveTypes = [] } = useObjectives()
  // Looked up rather than hard-coded: it only exists once the API has it.
  const referralType = objectiveTypes.find((type) => type.is_referral)
  const referralAvailable = !!referralType && !hasReferral
  const isReferral = kind === 'referral'

  const selected = guilds.find((guild) => guild.discord_guild_uuid === selectedUuid) ?? null

  const selectGuild = (guild: DiscordGuildRegister) => {
    if (!guild.is_registered) {
      // Inviting the bot leaves the app; save the draft so it survives.
      onBeforeLeave()
      void startOAuth(discordAddBotUrl(guild.discord_guild_uuid ?? undefined))
      return
    }
    setSelectedUuid(guild.discord_guild_uuid)
  }

  // Connecting leaves the app; save the draft and reopen the wizard on return.
  const connect = () => {
    // On the web this opens a new tab and the wizard stays open; the servers
    // list refreshes when the connection arrives. Only if it has to leave the
    // page is the draft saved for the way back.
    void connectPlatform(
      { name: PLATFORM, auth_url: platform?.auth_url },
      {
        beforeLeave: () => {
          setLeaving(true)
          onBeforeLeave()
          markWizardResume()
        },
      },
    ).finally(() => setLeaving(false))
  }

  const confirm = () => {
    if (isReferral) {
      if (!referralType) return
      onCreate(
        new GiveawayObjective({
          entries_worth: Number(entriesWorth),
          objective_id: referralType.id,
          objective_data_uuid: 'referral',
          objective_data_name: 'Referral',
          name: 'Refer a friend',
          objective: referralType,
        }),
      )
      return
    }
    if (!selected) return
    onCreate(
      new GiveawayObjective({
        entries_worth: Number(entriesWorth),
        objective_data_uuid: selected.discord_guild_uuid,
        objective_data_name: selected.name,
        objective_id: 1,
        objective: {
          endpoint_action: 'join',
          endpoint: 'guild',
          external_platform_id: 1,
          external_platform: { id: 1, name: 'discord' },
        },
      }),
    )
  }

  return (
    <div className="content-section">
      <div className="field-row with-margin large-margin new-objective-fields">
        <div className="field half">
          <div className="field-name">Add a new entry option</div>
          <Dropdown<string>
            label="Entry platform"
            icon={isReferral ? <ReferralIcon /> : <img src="/icons/discord.png" alt="" />}
            value={kind}
            options={[
              { value: 'discord', label: 'Discord' },
              {
                value: 'referral',
                label: hasReferral ? 'Referral (already added)' : 'Referral',
                disabled: !referralAvailable,
              },
              { value: 'steam', label: 'Steam (Coming soon)', disabled: true },
              { value: 'twitch', label: 'Twitch (Coming soon)', disabled: true },
            ]}
            onChange={(value) => {
              if (value === 'discord' || value === 'referral') setKind(value)
            }}
          />
        </div>

        {/* A first-come referral earns a key, not entries. */}
        {isReferral && firstEntries ? null : (
          <div className="field half">
            <label className="field-name" htmlFor="objective-entries-worth">
              {isReferral ? 'Entries per referral' : 'Entries for completing'}
            </label>
            <div className="field-box">
              <div className="field-box-prepend">
                <img src="/icons/logo.png" width={28} alt="" />
              </div>
              <input
                id="objective-entries-worth"
                className="field-box-input upper text-center"
                type="number"
                min={1}
                placeholder="Number of entries"
                value={entriesWorth}
                onChange={(event) => setEntriesWorth(Math.max(1, Number(event.target.value) || 1))}
              />
              <div className="field-box-append upper">
                {Number(entriesWorth) === 1 ? 'Entry' : 'Entries'}
              </div>
            </div>
          </div>
        )}
      </div>

      {isReferral && firstEntries ? (
        <div className="content-row small with-margin large-margin">
          Everyone gets a personal referral link for this giveaway. The first time someone enters
          through it, the link's owner gets a key — even if they haven't entered any other way.
          Later referrals don't earn more keys.
        </div>
      ) : isReferral ? (
        <div className="content-row small with-margin large-margin">
          Everyone gets a personal referral link for this giveaway. Each person who enters through
          it earns the link's owner {entriesWorth}{' '}
          {Number(entriesWorth) === 1 ? 'entry' : 'entries'}, with no limit. Sharing the link
          doesn't require entering another way first.
        </div>
      ) : (
        <>
          <div className="content-row heading-text with-margin large-margin">
            Which server should they join?
          </div>

          <div className="fields with-margin large-margin">
            {isLoading
              ? Array.from({ length: 3 }, (_, index) => (
                  <div className="field-row with-margin" key={index} aria-hidden="true">
                    <div className="field">
                      <div className="field-box button is-skeleton">
                        <div className="field-box-content left grow">
                          <SkeletonLine width={`${[46, 34, 58][index]}%`} />
                        </div>
                        <div className="field-box-content">
                          <SkeletonLine width="6em" />
                        </div>
                        <div className="field-box-append">
                          <div
                            className="skeleton"
                            style={{ width: 24, height: 24, borderRadius: 6 }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              : null}

            {!isLoading && !connected ? (
              <>
                <div className="content-row small upper with-margin">
                  Connect your {platformName(PLATFORM)} account to choose a server.
                </div>
                <div className="field-row with-margin center">
                  <div className="field half button">
                    <button
                      type="button"
                      className="field-box"
                      onClick={connect}
                      disabled={leaving}
                    >
                      <div className="field-box-prepend">
                        {leaving ? (
                          <span className="spinner-inline" aria-hidden="true" />
                        ) : (
                          <img src={`/icons/${PLATFORM}.png`} alt="" />
                        )}
                      </div>
                      <div className="field-box-content upper">
                        {leaving ? 'Redirecting…' : `Connect ${platformName(PLATFORM)}`}
                      </div>
                    </button>
                  </div>
                </div>
              </>
            ) : null}

            {!isLoading && connected && guilds.length === 0 ? (
              <div className="content-row small upper">
                No Discord servers found that you can manage.
              </div>
            ) : null}

            {guilds.map((guild) => (
              <div className="field-row with-margin" key={guild.discord_guild_uuid}>
                <div className="field">
                  <button
                    type="button"
                    onClick={() => selectGuild(guild)}
                    className={cn(
                      'field-box button',
                      guild.discord_guild_uuid === selectedUuid && 'selected',
                      guild.discord_guild_uuid !== selectedUuid &&
                        guild.is_registered &&
                        'confirmed',
                    )}
                  >
                    <div className="field-box-content left grow">{guild.name}</div>
                    <div className="field-box-content">
                      {guild.member_count ? (
                        <span>
                          {guild.member_count.toLocaleString()}{' '}
                          {guild.member_count === 1 ? 'Member' : 'Members'}
                        </span>
                      ) : (
                        <span style={{ fontSize: 12 }}>ID: {guild.discord_guild_uuid}</span>
                      )}
                    </div>
                    <div
                      className="field-box-append"
                      style={{
                        visibility:
                          guild.discord_guild_uuid === selectedUuid ? 'hidden' : undefined,
                      }}
                    >
                      <img
                        src={
                          guild.is_registered
                            ? '/icons/confirmed_guild.png'
                            : '/icons/add_guild.png'
                        }
                        alt={guild.is_registered ? 'Bot installed' : 'Install the bot'}
                      />
                    </div>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="footer">
        <div className="cta-row">
          <button type="button" className="cta" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="cta next"
            onClick={confirm}
            disabled={isReferral ? !referralAvailable : !selected}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  )
}
